import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
	Clock,
	CheckCircle,
	XCircle,
	PlayCircle,
	Send,
	MessageSquare,
	FileText,
	Upload,
	User,
	AlertCircle,
	RefreshCw,
} from 'lucide-react'
import { Button } from '@components/common'
import taskService from '@services/taskService'
import './ActivityHistoryTab.scss'

/**
 * ActivityHistoryTab Component
 *
 * Displays comprehensive activity history including:
 * - Workflow step transitions with timestamps
 * - Task notes history (all notes chronologically)
 * - Document upload history
 * - System events (status changes, assignments)
 */
const ActivityHistoryTab = ({
	task,
	workflowInfo,
	taskId,
}) => {
	const [notes, setNotes] = useState([])
	const [loadingNotes, setLoadingNotes] = useState(true)
	const [notesError, setNotesError] = useState(null)

	// Fetch notes
	useEffect(() => {
		if (taskId) {
			fetchNotes()
		}
	}, [taskId])

	const fetchNotes = async () => {
		try {
			setLoadingNotes(true)
			setNotesError(null)
			const response = await taskService.getNotes(taskId)
			if (response.status === 'success' || response.success) {
				setNotes(response.data || [])
			}
		} catch (err) {
			console.error('Error fetching notes:', err)
			setNotesError('Failed to load notes')
		} finally {
			setLoadingNotes(false)
		}
	}

	// Format date
	const formatDate = (dateString) => {
		if (!dateString) return 'N/A'
		const date = new Date(dateString)
		return date.toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	}

	// Format relative time
	const formatRelativeTime = (dateString) => {
		if (!dateString) return ''
		const date = new Date(dateString)
		const now = new Date()
		const diffMs = now - date
		const diffMins = Math.floor(diffMs / 60000)
		const diffHours = Math.floor(diffMs / 3600000)
		const diffDays = Math.floor(diffMs / 86400000)

		if (diffMins < 1) return 'Just now'
		if (diffMins < 60) return `${diffMins}m ago`
		if (diffHours < 24) return `${diffHours}h ago`
		if (diffDays < 7) return `${diffDays}d ago`
		return formatDate(dateString)
	}

	// Get icon for workflow action
	const getActionIcon = (action) => {
		switch (action?.toLowerCase()) {
			case 'approved':
			case 'approval':
				return <CheckCircle size={18} />
			case 'rejected':
			case 'rejection':
				return <XCircle size={18} />
			case 'started':
			case 'start':
				return <PlayCircle size={18} />
			case 'submitted':
			case 'submit':
				return <Send size={18} />
			default:
				return <Clock size={18} />
		}
	}

	// Get icon for note type
	const getNoteIcon = (noteType) => {
		switch (noteType) {
			case 'approval':
				return <CheckCircle size={16} />
			case 'rejection':
				return <XCircle size={16} />
			case 'status_change':
				return <RefreshCw size={16} />
			case 'system':
				return <AlertCircle size={16} />
			default:
				return <MessageSquare size={16} />
		}
	}

	// Get note type label
	const getNoteTypeLabel = (noteType) => {
		switch (noteType) {
			case 'approval':
				return 'Approval'
			case 'rejection':
				return 'Rejection'
			case 'status_change':
				return 'Status Change'
			case 'system':
				return 'System'
			default:
				return 'Comment'
		}
	}

	// Combine and sort all activities
	const getAllActivities = () => {
		const activities = []

		// Add workflow history entries
		if (workflowInfo?.workflow_history) {
			workflowInfo.workflow_history.forEach((entry, index) => {
				activities.push({
					id: `workflow-${index}`,
					type: 'workflow',
					action: entry.action,
					user_name: entry.user_name,
					step_name: entry.step_name,
					notes: entry.notes,
					timestamp: entry.timestamp,
				})
			})
		}

		// Add task history from task object
		if (task?.workflow_history) {
			task.workflow_history.forEach((entry, index) => {
				// Avoid duplicates if already in workflowInfo
				const isDuplicate = activities.some(a =>
					a.type === 'workflow' &&
					a.timestamp === entry.timestamp &&
					a.action === entry.action
				)
				if (!isDuplicate) {
					activities.push({
						id: `task-workflow-${index}`,
						type: 'workflow',
						action: entry.action,
						user_name: entry.user_name,
						step_name: entry.step_name,
						notes: entry.notes,
						timestamp: entry.timestamp,
					})
				}
			})
		}

		// Add notes
		notes.forEach(note => {
			activities.push({
				id: `note-${note.id}`,
				type: 'note',
				noteType: note.note_type,
				user_name: note.user_name,
				user_role: note.user_role,
				content: note.note,
				timestamp: note.created_at,
				step_name: note.step_name,
			})
		})

		// Sort by timestamp (newest first)
		return activities.sort((a, b) => {
			const dateA = new Date(a.timestamp || 0)
			const dateB = new Date(b.timestamp || 0)
			return dateB - dateA
		})
	}

	const activities = getAllActivities()
	const workflowHistory = workflowInfo?.workflow_history || task?.workflow_history || []

	if (!task) {
		return (
			<div className="activity-history-tab activity-history-tab--empty">
				<Clock size={48} />
				<p>No activity data available</p>
			</div>
		)
	}

	return (
		<div className="activity-history-tab">
			{/* Workflow History Section */}
			{workflowHistory.length > 0 && (
				<div className="activity-section">
					<div className="activity-section__header">
						<h3>
							<FileText size={18} />
							Workflow History
						</h3>
						<span className="activity-section__count">{workflowHistory.length} events</span>
					</div>

					<div className="workflow-history">
						{workflowHistory.map((entry, index) => (
							<div key={index} className={`history-item history-item--${entry.action?.toLowerCase()}`}>
								<div className="history-item__icon">
									{getActionIcon(entry.action)}
								</div>
								<div className="history-item__content">
									<div className="history-item__header">
										<span className="history-item__step">
											{entry.step_name || `Step ${index + 1}`}
										</span>
										<span className={`history-item__action history-item__action--${entry.action?.toLowerCase()}`}>
											{entry.action}
										</span>
									</div>
									<div className="history-item__meta">
										{entry.user_name && (
											<span className="history-item__user">
												<User size={12} />
												{entry.user_name}
											</span>
										)}
										<span className="history-item__time">
											<Clock size={12} />
											{formatDate(entry.timestamp)}
										</span>
									</div>
									{entry.notes && (
										<p className="history-item__notes">"{entry.notes}"</p>
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Notes History Section */}
			<div className="activity-section">
				<div className="activity-section__header">
					<h3>
						<MessageSquare size={18} />
						Notes & Comments
					</h3>
					<div className="activity-section__actions">
						<span className="activity-section__count">{notes.length} notes</span>
						<Button variant="ghost" size="small" onClick={fetchNotes} disabled={loadingNotes}>
							<RefreshCw size={14} className={loadingNotes ? 'spinning' : ''} />
						</Button>
					</div>
				</div>

				{loadingNotes ? (
					<div className="loading-state">
						<div className="spinner"></div>
						<p>Loading notes...</p>
					</div>
				) : notesError ? (
					<div className="error-state">
						<AlertCircle size={24} />
						<p>{notesError}</p>
						<Button variant="outline" size="small" onClick={fetchNotes}>
							Retry
						</Button>
					</div>
				) : notes.length === 0 ? (
					<div className="empty-state">
						<MessageSquare size={32} />
						<p>No notes or comments yet</p>
					</div>
				) : (
					<div className="notes-history">
						{notes.map(note => (
							<div key={note.id} className={`note-item note-item--${note.note_type}`}>
								<div className="note-item__avatar">
									{note.user_name ? note.user_name.charAt(0).toUpperCase() : 'S'}
								</div>
								<div className="note-item__content">
									<div className="note-item__header">
										<span className="note-item__user">
											{note.user_name || 'System'}
										</span>
										<span className={`note-item__type note-item__type--${note.note_type}`}>
											{getNoteIcon(note.note_type)}
											{getNoteTypeLabel(note.note_type)}
										</span>
									</div>
									{note.user_role && (
										<span className="note-item__role">{note.user_role}</span>
									)}
									<p className="note-item__text">{note.note}</p>
									<div className="note-item__footer">
										<span className="note-item__time">
											{formatRelativeTime(note.created_at)}
										</span>
										{note.step_name && (
											<span className="note-item__step">
												at {note.step_name}
											</span>
										)}
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Combined Timeline Section */}
			{activities.length > 0 && (
				<div className="activity-section">
					<div className="activity-section__header">
						<h3>
							<Clock size={18} />
							All Activity Timeline
						</h3>
						<span className="activity-section__count">{activities.length} total activities</span>
					</div>

					<div className="timeline-list">
						{activities.slice(0, 50).map(activity => (
							<div key={activity.id} className={`timeline-entry timeline-entry--${activity.type}`}>
								<div className="timeline-entry__marker">
									{activity.type === 'workflow'
										? getActionIcon(activity.action)
										: getNoteIcon(activity.noteType)
									}
								</div>
								<div className="timeline-entry__content">
									<div className="timeline-entry__header">
										{activity.type === 'workflow' ? (
											<>
												<span className="timeline-entry__title">
													{activity.step_name || 'Workflow Action'}
												</span>
												<span className={`timeline-entry__badge timeline-entry__badge--${activity.action?.toLowerCase()}`}>
													{activity.action}
												</span>
											</>
										) : (
											<>
												<span className="timeline-entry__title">
													{activity.user_name || 'System'}
												</span>
												<span className={`timeline-entry__badge timeline-entry__badge--${activity.noteType}`}>
													{getNoteTypeLabel(activity.noteType)}
												</span>
											</>
										)}
									</div>
									{(activity.notes || activity.content) && (
										<p className="timeline-entry__text">
											{activity.notes || activity.content}
										</p>
									)}
									<span className="timeline-entry__time">
										{formatRelativeTime(activity.timestamp)}
									</span>
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Empty state when no activities */}
			{activities.length === 0 && !loadingNotes && (
				<div className="activity-history-tab__empty">
					<Clock size={48} />
					<h3>No Activity Yet</h3>
					<p>Activity history will appear here as you work on this task.</p>
				</div>
			)}
		</div>
	)
}

ActivityHistoryTab.propTypes = {
	task: PropTypes.object,
	workflowInfo: PropTypes.object,
	taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default ActivityHistoryTab
